import { useEffect, useState, type ReactNode } from "react";
import { Link, Outlet, useLocation, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type Project, type User, type Workspace } from "../lib/api";
import LoadingBar from "./LoadingBar";
import PageSpinner from "./PageSpinner";
import { LoadingButton } from "./Spinner";
import UserMenu from "./UserMenu";
import Topbar from "./Topbar";
import CommandPalette from "./CommandPalette";
import EnvironmentsModal from "./EnvironmentsModal";
import AgentsModal from "./AgentsModal";
import WorkspaceSettingsModal from "./WorkspaceSettingsModal";
import { useHotkey } from "../lib/keybindings";

const ACTIVE_KEY = "rest.activeWorkspace";
const ACTIVE_PROJECT_KEY_PREFIX = "rest.activeProject:";

export function readActiveWorkspaceId(): string | null {
  try { return localStorage.getItem(ACTIVE_KEY); } catch { return null; }
}
export function writeActiveWorkspaceId(id: string | null): void {
  try {
    if (id) localStorage.setItem(ACTIVE_KEY, id);
    else localStorage.removeItem(ACTIVE_KEY);
  } catch { /* ignore */ }
}
export function readActiveProjectId(workspaceId: string): string | null {
  try { return localStorage.getItem(ACTIVE_PROJECT_KEY_PREFIX + workspaceId); } catch { return null; }
}
export function writeActiveProjectId(workspaceId: string, id: string | null): void {
  try {
    if (id) localStorage.setItem(ACTIVE_PROJECT_KEY_PREFIX + workspaceId, id);
    else localStorage.removeItem(ACTIVE_PROJECT_KEY_PREFIX + workspaceId);
  } catch { /* ignore */ }
}

export default function Layout({ user }: { user: User }) {
  const qc = useQueryClient();
  const workspaces = useQuery({ queryKey: ["workspaces"], queryFn: () => api.workspaces.list() });
  const list = workspaces.data?.workspaces ?? [];

  // Workspace can be deep-linked via ?w=ws_… — the URL wins, then localStorage,
  // then the first workspace. On every render we resync localStorage so a
  // pasted link sticks across reloads even after the query param is gone.
  const [sp] = useSearchParams();
  const urlW = sp.get("w") ?? "";
  const stored = readActiveWorkspaceId();
  const activeId =
    list.find((w) => w.id === urlW)?.id ??
    list.find((w) => w.id === stored)?.id ??
    list[0]?.id ??
    null;
  useEffect(() => {
    if (urlW && list.some((w) => w.id === urlW)) writeActiveWorkspaceId(urlW);
  }, [urlW, list]);

  const create = useMutation({
    mutationFn: (name: string) => api.workspaces.create(name),
    onSuccess: (w) => { writeActiveWorkspaceId(w.id); qc.invalidateQueries({ queryKey: ["workspaces"] }); },
  });

  if (workspaces.isError) {
    return (
      <FullPage>
        <LoadingBar />
        <div className="grid h-full place-items-center bg-bg p-6">
          <div className="card max-w-sm p-6 text-center space-y-3">
            <p className="text-sm muted">Your session expired.</p>
            <a className="btn-primary inline-flex" href="/login">Sign in again</a>
          </div>
        </div>
      </FullPage>
    );
  }
  if (!workspaces.isSuccess) {
    return (
      <FullPage>
        <LoadingBar />
        <PageSpinner />
      </FullPage>
    );
  }

  if (list.length === 0) {
    return (
      <FullPage>
        <LoadingBar />
        <div className="grid h-full place-items-center bg-bg p-6">
          <div className="card max-w-md w-full p-6 space-y-4 text-center">
            <Logo />
            <h1 className="text-xl font-semibold">Create your first workspace</h1>
            <p className="text-sm muted">
              A workspace holds your collections of API requests. Invite teammates
              to share them — every save is a versioned revision you can roll back.
            </p>
            <CreateWorkspaceForm onCreate={(name) => create.mutate(name)} pending={create.isPending} />
          </div>
        </div>
      </FullPage>
    );
  }

  const active = list.find((w) => w.id === activeId) ?? list[0];

  return (
    <LayoutInner user={user} workspaces={list} active={active} />
  );
}

function LayoutInner({ user, workspaces, active }: { user: User; workspaces: Workspace[]; active: Workspace }) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [envsOpen, setEnvsOpen] = useState(false);
  const [agentsOpen, setAgentsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  useHotkey("Mod+k", () => setPaletteOpen((v) => !v));
  // Cmd/Ctrl+E opens the environments modal so users can switch or edit
  // env vars without leaving the request they're working on.
  useHotkey("Mod+e", () => setEnvsOpen((v) => !v));

  const projectsQ = useQuery({
    queryKey: ["projects", active.id],
    queryFn: () => api.projects.list(active.id),
  });
  const projects = projectsQ.data?.projects ?? [];
  const [sp] = useSearchParams();
  const urlP = sp.get("prj") ?? "";
  const stored = readActiveProjectId(active.id);
  const activeProjectId =
    projects.find((p) => p.id === urlP)?.id
    ?? projects.find((p) => p.id === stored)?.id
    ?? projects.find((p) => !!p.is_default)?.id
    ?? projects[0]?.id
    ?? null;
  const activeProject = projects.find((p) => p.id === activeProjectId) ?? null;
  useEffect(() => {
    if (urlP && projects.some((p) => p.id === urlP)) writeActiveProjectId(active.id, urlP);
  }, [urlP, projects, active.id]);

  return (
    <FullPage>
      <LoadingBar />
      <MobileTopbar
        active={active}
        activeProject={activeProject}
        onPalette={() => setPaletteOpen(true)}
      />
      <Topbar
        workspaces={workspaces}
        activeWorkspace={active}
        projects={projects}
        activeProject={activeProject}
        onManageEnvs={() => setEnvsOpen(true)}
        onManageAgents={() => setAgentsOpen(true)}
        onManageWorkspace={() => setSettingsOpen(true)}
      />
      <div className="flex h-full min-h-0">
        <main className="flex-1 min-w-0 overflow-hidden bg-bg">
          <Outlet context={{ workspace: active, project: activeProject, user }} />
        </main>
      </div>
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        workspace={active}
        onManageEnvs={() => setEnvsOpen(true)}
        onManageAgents={() => setAgentsOpen(true)}
        onManageWorkspace={() => setSettingsOpen(true)}
      />
      <EnvironmentsModal workspace={active} open={envsOpen} onClose={() => setEnvsOpen(false)} />
      <AgentsModal workspace={active} open={agentsOpen} onClose={() => setAgentsOpen(false)} />
      <WorkspaceSettingsModal workspace={active} open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </FullPage>
  );
}

function MobileTopbar({ active, activeProject, onPalette }: { active: Workspace; activeProject: Project | null; onPalette: () => void }) {
  return (
    <header className="sm:hidden flex items-center gap-2 border-b border-border bg-surface px-3 py-2">
      <Link to="/" className="flex items-center gap-2 min-w-0">
        <img src="/logo.png?v=2" alt="" className="h-7 w-7 shrink-0" />
        <div className="min-w-0">
          <div className="text-xs font-semibold truncate">{active.name}</div>
          {activeProject && <div className="text-[10px] muted-strong truncate">{activeProject.name}</div>}
        </div>
      </Link>
      <button
        type="button"
        onClick={onPalette}
        aria-label="Search"
        className="ml-auto h-10 w-10 grid place-items-center rounded hover:bg-surface-2 text-text-dim hover:text-text"
      >
        <SearchIcon />
      </button>
    </header>
  );
}

function FullPage({ children }: { children: ReactNode }) {
  return <div className="flex flex-col h-full">{children}</div>;
}

function Logo() {
  return (
    <img src="/logo.png?v=2" alt="rest" className="mx-auto h-24 w-24" />
  );
}

function CreateWorkspaceForm({ onCreate, pending }: { onCreate: (name: string) => void; pending: boolean }) {
  const [name, setName] = useState("My workspace");
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => { e.preventDefault(); onCreate(name.trim() || "My workspace"); }}
    >
      <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      <LoadingButton
        type="submit"
        className="btn-primary w-full justify-center"
        pending={pending}
        pendingLabel="Creating…"
      >
        Create workspace
      </LoadingButton>
    </form>
  );
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="1.8" />
      <path d="M16 16l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}
